import React, { useState } from 'react';
import { AlertCircle, Moon, Utensils } from 'lucide-react';
import { buildApiUrl } from '../config/appConfig';
import { useDocumentMetadata } from '../hooks/useDocumentMetadata';

interface HealthPlanResponse {
  diet_plan: {
    caloric_intake: number;
    macronutrients: {
      carbohydrates: string;
      proteins: string;
      fats: string;
    };
    meal_plan: Record<
      string,
      {
        time: string;
        items: string[];
      }
    >;
  };
  sleep_routine: {
    bedtime: string;
    wake_time: string;
    pre_sleep_activities: string[];
  };
}

interface MealPlanEntry {
  time: string;
  items: string[];
}

interface HealthPlanFormState {
  age: string;
  weight: string;
  height: string;
  activityLevel: string;
  dietaryRestrictions: string;
  sleepIssues: string;
}

const initialFormState: HealthPlanFormState = {
  age: '',
  weight: '',
  height: '',
  activityLevel: '',
  dietaryRestrictions: '',
  sleepIssues: '',
};

function toDisplayList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).filter(Boolean);
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).map(([key, item]) => `${formatLabel(key)}: ${String(item)}`);
  }

  if (typeof value === 'string') {
    return value
      .split(/,|\n|;/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function normalizeMealPlan(mealPlan: unknown): Record<string, MealPlanEntry> {
  if (!mealPlan || typeof mealPlan !== 'object' || Array.isArray(mealPlan)) {
    return {};
  }

  return Object.entries(mealPlan as Record<string, unknown>).reduce<Record<string, MealPlanEntry>>(
    (accumulator, [mealName, mealValue]) => {
      if (mealValue && typeof mealValue === 'object' && !Array.isArray(mealValue)) {
        const entry = mealValue as Record<string, unknown>;
        accumulator[mealName] = {
          time: typeof entry.time === 'string' ? entry.time : 'Flexible',
          items: toDisplayList(entry.items ?? entry.recommendations ?? entry.foods),
        };
        return accumulator;
      }

      accumulator[mealName] = {
        time: 'Flexible',
        items: toDisplayList(mealValue),
      };
      return accumulator;
    },
    {}
  );
}

function normalizeHealthPlan(rawPlan: unknown): HealthPlanResponse {
  const healthPlan = (rawPlan && typeof rawPlan === 'object' ? rawPlan : {}) as Record<string, unknown>;
  const dietPlan = ((healthPlan.diet_plan || healthPlan.dietPlan || {}) as Record<string, unknown>);
  const sleepRoutine = ((healthPlan.sleep_routine || healthPlan.sleepRoutine || {}) as Record<string, unknown>);
  const macronutrients = ((dietPlan.macronutrients || {}) as Record<string, unknown>);

  return {
    diet_plan: {
      caloric_intake: Number(dietPlan.caloric_intake ?? dietPlan.calories ?? 0),
      macronutrients: {
        carbohydrates: String(macronutrients.carbohydrates ?? macronutrients.carbs ?? 'Not specified'),
        proteins: String(macronutrients.proteins ?? macronutrients.protein ?? 'Not specified'),
        fats: String(macronutrients.fats ?? macronutrients.fat ?? 'Not specified'),
      },
      meal_plan: normalizeMealPlan(dietPlan.meal_plan ?? dietPlan.meals),
    },
    sleep_routine: {
      bedtime: String(sleepRoutine.bedtime ?? sleepRoutine.sleep_time ?? 'Not specified'),
      wake_time: String(sleepRoutine.wake_time ?? sleepRoutine.wakeTime ?? 'Not specified'),
      pre_sleep_activities: toDisplayList(
        sleepRoutine.pre_sleep_activities ?? sleepRoutine.preSleepActivities
      ),
    },
  };
}

function formatLabel(value: string) {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

const HealthPlansPage: React.FC = () => {
  const [formState, setFormState] = useState<HealthPlanFormState>(initialFormState);
  const [healthPlan, setHealthPlan] = useState<HealthPlanResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useDocumentMetadata(
    'Personalized Wellness Plans',
    'Generate personalized meal and sleep recommendations with MediInsight AI.'
  );

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormState((currentState) => ({ ...currentState, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);
    setHealthPlan(null);

    try {
      const response = await fetch(buildApiUrl('/health-plans'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formState),
      });

      if (!response.ok) {
        throw new Error('Unable to generate a health plan.');
      }

      const data = await response.json();
      setHealthPlan(normalizeHealthPlan(data.healthPlan));
    } catch (error) {
      setErrorMessage('We could not generate a plan right now. Please try again in a moment.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <main className="container mx-auto px-4">
        <div className="mx-auto mb-10 max-w-4xl text-center">
          <h1 className="text-4xl font-bold text-slate-900">Personalized Wellness Plans</h1>
          <p className="mt-4 text-lg text-slate-600">
            Create nutrition and sleep recommendations tailored to lifestyle, dietary needs, and current sleep challenges.
          </p>
        </div>

        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
            <h2 className="text-2xl font-semibold text-slate-900">Health Questionnaire</h2>
            <p className="mt-2 text-sm text-slate-600">
              Enter baseline information and MediInsight AI will return a structured wellness plan.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <Field label="Age" htmlFor="age">
                <input
                  id="age"
                  name="age"
                  type="number"
                  min="1"
                  max="120"
                  value={formState.age}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                  required
                />
              </Field>

              <Field label="Weight (kg)" htmlFor="weight">
                <input
                  id="weight"
                  name="weight"
                  type="number"
                  min="1"
                  max="500"
                  step="0.1"
                  value={formState.weight}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                  required
                />
              </Field>

              <Field label="Height (cm)" htmlFor="height">
                <input
                  id="height"
                  name="height"
                  type="number"
                  min="1"
                  max="300"
                  value={formState.height}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                  required
                />
              </Field>

              <Field label="Activity level" htmlFor="activityLevel">
                <select
                  id="activityLevel"
                  name="activityLevel"
                  value={formState.activityLevel}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                  required
                >
                  <option value="">Select an option</option>
                  <option value="sedentary">Sedentary</option>
                  <option value="lightly active">Lightly active</option>
                  <option value="moderately active">Moderately active</option>
                  <option value="very active">Very active</option>
                  <option value="extra active">Extra active</option>
                </select>
              </Field>

              <Field label="Dietary restrictions" htmlFor="dietaryRestrictions">
                <input
                  id="dietaryRestrictions"
                  name="dietaryRestrictions"
                  type="text"
                  value={formState.dietaryRestrictions}
                  onChange={handleInputChange}
                  placeholder="Vegetarian, gluten-free, nut allergy"
                  className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                />
              </Field>

              <Field label="Sleep concerns" htmlFor="sleepIssues">
                <textarea
                  id="sleepIssues"
                  name="sleepIssues"
                  rows={4}
                  value={formState.sleepIssues}
                  onChange={handleInputChange}
                  placeholder="Describe patterns like insomnia, waking during the night, or inconsistent sleep."
                  className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                />
              </Field>

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full rounded-full px-6 py-3 font-semibold text-white transition ${
                  isLoading
                    ? 'cursor-not-allowed bg-slate-400'
                    : 'bg-sky-600 hover:bg-sky-700'
                }`}
              >
                {isLoading ? 'Generating Plan...' : 'Generate Wellness Plan'}
              </button>
            </form>
          </div>

          <div className="space-y-6">
            {errorMessage && (
              <div
                className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-rose-700"
                role="alert"
              >
                <AlertCircle className="mr-2 inline h-4 w-4" />
                {errorMessage}
              </div>
            )}

            {!healthPlan && !errorMessage && (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
                Your generated wellness plan will appear here with diet guidance, calorie targets, and bedtime routines.
              </div>
            )}

            {healthPlan && (
              <div className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
                <h2 className="text-2xl font-semibold text-slate-900">Your AI-Generated Wellness Plan</h2>

                <section className="mt-6">
                  <h3 className="flex items-center gap-2 text-xl font-semibold text-sky-700">
                    <Utensils className="h-5 w-5" />
                    Diet Plan
                  </h3>
                  <p className="mt-3 text-slate-700">
                    Daily caloric intake: <span className="font-semibold">{healthPlan.diet_plan.caloric_intake}</span> calories
                  </p>
                  <div className="mt-4 grid gap-4 md:grid-cols-3">
                    <MetricCard label="Carbohydrates" value={healthPlan.diet_plan.macronutrients.carbohydrates} />
                    <MetricCard label="Proteins" value={healthPlan.diet_plan.macronutrients.proteins} />
                    <MetricCard label="Fats" value={healthPlan.diet_plan.macronutrients.fats} />
                  </div>

                  <div className="mt-6 space-y-4">
                    {Object.entries(healthPlan.diet_plan.meal_plan).map(([mealName, details]) => (
                      <div key={mealName} className="rounded-2xl bg-slate-50 p-4">
                        <h4 className="font-semibold capitalize text-slate-900">
                          {mealName} <span className="text-slate-500">({details.time})</span>
                        </h4>
                        <ul className="mt-2 list-disc pl-5 text-slate-600">
                          {details.items.map((item) => (
                            <li key={`${mealName}-${item}`}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="mt-8">
                  <h3 className="flex items-center gap-2 text-xl font-semibold text-sky-700">
                    <Moon className="h-5 w-5" />
                    Sleep Routine
                  </h3>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <MetricCard label="Suggested bedtime" value={healthPlan.sleep_routine.bedtime} />
                    <MetricCard label="Suggested wake time" value={healthPlan.sleep_routine.wake_time} />
                  </div>
                  <div className="mt-4 rounded-2xl bg-slate-50 p-4">
                    <h4 className="font-semibold text-slate-900">Pre-sleep activities</h4>
                    <ul className="mt-2 list-disc pl-5 text-slate-600">
                      {healthPlan.sleep_routine.pre_sleep_activities.map((activity) => (
                        <li key={activity}>{activity}</li>
                      ))}
                    </ul>
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const Field: React.FC<{
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}> = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-700">
      {label}
    </label>
    {children}
  </div>
);

const MetricCard: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="rounded-2xl bg-sky-50 p-4">
    <p className="text-sm text-slate-500">{label}</p>
    <p className="mt-2 font-semibold text-slate-900">{value}</p>
  </div>
);

export default HealthPlansPage;
